import { Component, type ReactNode, type ErrorInfo } from 'react';
import { CloudSlash, WarningCircle } from '@phosphor-icons/react';
import type { ApiError } from '../lib/api-client';

interface Props {
  children: ReactNode;
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'status' in error && typeof (error as ApiError).status === 'number';
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`[ErrorBoundary] ${this.props.sectionName || 'Section'} crashed:`, error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { error } = this.state;
      const apiError = isApiError(error);
      const status = apiError ? (error as ApiError).status : undefined;

      const title = apiError
        ? status === 500
          ? 'Server error'
          : status === 403
            ? 'Access denied'
            : status === 404
              ? 'Not found'
              : 'Unable to load data'
        : 'Something went wrong';

      const sectionLabel = this.props.sectionName
        ? `The ${this.props.sectionName} section`
        : 'This section';

      const description = apiError
        ? `${sectionLabel} could not load data from the server (${status}).`
        : `${sectionLabel} encountered an error.`;

      const Icon = apiError ? CloudSlash : WarningCircle;

      return (
        <div role="alert" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center" data-testid="error-boundary-fallback">
          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mb-4">
            <Icon size={28} weight="bold" className="text-red-500" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-gray-200 mb-2">{title}</h2>
          <p className="text-sm text-gray-300 mb-4 max-w-md">
            {description}
          </p>
          <button
            onClick={this.handleReset}
            className="px-4 py-2 bg-teal-500/20 border border-teal-500/30 text-teal-400 text-sm font-medium rounded-lg cursor-pointer hover:bg-teal-500/30 transition-all"
            data-testid="error-boundary-reset"
          >
            {apiError ? 'Try Again' : 'Reload Section'}
          </button>
          {error && !apiError && (
            <details className="mt-4 text-left max-w-lg">
              <summary className="text-xs text-gray-600 cursor-pointer">Technical Details</summary>
              <pre className="mt-2 text-xs text-red-400/70 bg-black/20 p-3 rounded-lg overflow-auto max-h-32">
                {error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
